import {Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {combineLatest, Observable, of} from 'rxjs';
import {finalize, map, mergeMap, take, takeWhile, tap} from 'rxjs/operators';
import {DirtyComponent} from '@supoui/core/guards/dirty-check.guard';
import {resumeAnimation} from '@supoui/shared/components/resume-animation';
import {AbstractFormGroupComponent} from '@supoui/shared/components/abstract-form-group.component';
import {SupplierInvoice} from '@supoui/shared/model/supplier-invoice';
import {SupplierInvoicesService} from '@supoui/core/services/supplier-invoices.service';
import {AccountService} from '@supoui/core/auth/account.service';
import {NGXLogger} from 'ngx-logger';
import {SupplierProfile} from '@supoui/shared/model/supplier-profile';
import {SupplierInvoiceItem} from '@supoui/shared/model/supplier-invoice-item';
import {SupoCaseInvoiceService} from '@supoui/core/services/supo-case-invoice.service';
import {SupoInvoice} from '@supoui/shared/model/supo-invoice';
import {CaseStateService} from '@supoui/core/state-services/case-state.service';
import {UploadComponent} from '@supoui/shared/components/upload/upload.component';
import {SupoDocument} from '@supoui/shared/model/supo-document';
import {CaseService} from '@supoui/core/services/case.service';
import {IServiceOrder} from '@supoui/shared/model/service-order';
import {Case} from '@supoui/shared/model/case';
import {SupoSeccaInvoiceMapper} from '@supoui/case/components/case-economy/supo-secca-invoice-mapper';
import {Location as UrlLocation} from '@angular/common';
import {SupplierInvoiceStatus} from '@supoui/shared/model/enumerations/supplier-invoice-status.enum';
import {FinanceBridgeService} from '@supoui/core/services/finance-bridge-service';
import {DropdownDictionary} from '@supoui/shared/model/dropdownDictionary';
import {IFinanceBridgeSupplierDetails} from '@supoui/shared/model/finance-bridge-supplier-details';
import {SupplierInvoiceStateService} from '@supoui/core/state-services/supplier-invoice-state.service';
import {SortOrderType} from '@supoui/shared/model/enumerations/sort-order-type.model';
import {SupoInvoiceStateService} from '@supoui/core/state-services/supo-invoice-state.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {SupoTypeEnum} from '@supoui/shared/model/enumerations/supo-type.enum';
import {ServiceTypeEnum} from '@supoui/shared/model/enumerations/service-type.enum';
import {InvoiceDocumentTypeEnum} from '@supoui/shared/model/enumerations/invoice-document-type.enum';
import {CaseDocumentEnum} from '@supoui/shared/model/case-document';
import {FileUtil, FileUtilValidationConfig} from '@supoui/shared/util/file-util';
import {AttachInvoiceDocument} from '@supoui/shared/model/attach-invoice-document';
import {calculateEstimatedDueDate} from './estimated-due-date-calculator';
import {NewInvoiceSuccessModalComponent} from '../new-invoice-success-modal/new-invoice-success-modal.component';
import {SupoCase} from '@supoui/shared/model/supo-case';
import {BatchBillingSuccessModalComponent} from '../batch-billing-success-modal/batch-billing-success-modal.component';
import {ConfirmModalComponent} from '../../confirm-modal/confirm-modal.component';
import {ModalDialogConfiguration} from '@supoui/shared/model/modal/modal-dialog-configuration';
import {TranslateService} from '@ngx-translate/core';
import {ChannelType} from '@supoui/shared/model/enumerations/channelType';
import {MasterListService} from '../../task-web-modal/services/masterlist-service';
import {SupplierProfileService} from '@supoui/core/services/supplier-profile.service';
import {AccountRoleService} from '@supoui/core/auth/account-role.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {SupoPendingInvoicesService} from "@supoui/core/services/supo-pending-invoices.service";
import {SupoType} from "@supoui/shared/model/supo-type";

@Component({
    selector: 'app-new-invoice',
    templateUrl: './new-invoice.component.html',
    styleUrls: ['./new-invoice.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: resumeAnimation,
    standalone: false
})
@UntilDestroy({ arrayName: 'subscriptions' })
export class NewInvoiceComponent extends AbstractFormGroupComponent implements OnInit, OnDestroy, DirtyComponent {
  static readonly BATCH_BILLING_TYPE_ID: number = 1;
  static readonly MODE_BATCH_BILLING = 'batchBilling';

  readonly acceptedTravelAndExpensesDocumentTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  readonly maxTravelAndExpensesDocuments = 20;

  @ViewChild('uploadComponentInstance', { static: false })
  public uploadComponentInstance: UploadComponent;

  @ViewChild('uploadTravelExpensesComponentInstance', { static: false })
  public uploadTravelExpensesReportComponentInstance: UploadComponent;

  @ViewChild('uploadMedicalTravelComponentInstance', { static: false })
  public uploadMedicalTravelComponentInstance: UploadComponent;

  supplierInvoice: SupplierInvoice;
  serviceOrders: IServiceOrder[];
  seccaCase: Case | null;
  state: string;
  mode: string;
  isSupplierPartner: boolean = false;
  isSupplierMedicalEscort = false;
  isMedicalEscortSelected = false;
  invoiceNoUsedMsg: string | undefined;
  dropDownSuppliers: DropdownDictionary[] = [];
  supplierDetails: IFinanceBridgeSupplierDetails | null;
  isSubmitRequest = false;
  otherInvoicesOnCase: SupplierInvoice[];
  sortedOn: any = { title: true };
  sortOrder: any = {};
  invoiceId: string | undefined;
  estimatedDueDate: Date | null;
  partnerSupoTypeId: number | undefined;
  medicalEscortSupoTypeId: number | undefined;
  saveInProgress = false;
  invoiceNoLookupInProgress = false;
  fileChanged = false;
  isDestroyed = false;
  draftCase: SupoCase | undefined;
  batchBillingCaseId: string | null;
  error: any;
  caseId: string;

  error404 = false;
  error503 = false;

  isErpCreditorDisabled = false;
  isSupoInvoiceFinanceBridgeError = false;

  invoiceFilesValidationResult: Map<string, string[]>;
  travelAndExpenseFilesValidationResult: Map<string, string[]>;
  medicalTravelReportFilesValidationResult: Map<string, string[]>;

  private mapper: SupoSeccaInvoiceMapper;
  private lastInvoiceEnquiry: any = { creditorId: null, invoiceNo: null };
  private supplierProfiles: SupplierProfile[] = [];
  private bypassDirtyCheck: boolean;

  get items(): UntypedFormArray {
    return this.fg.controls['items'] as UntypedFormArray;
  }

  constructor(
    private accountService: AccountService,
    private accountRoleService: AccountRoleService,
    private supoCaseInvoiceService: SupoCaseInvoiceService,
    private supplierInvoicesService: SupplierInvoicesService,
    private supoPendingInvoicesService: SupoPendingInvoicesService,
    private supplierProfileService: SupplierProfileService,
    private masterListService: MasterListService,
    private supplierInvoiceStateService: SupplierInvoiceStateService,
    private supoInvoiceStateService: SupoInvoiceStateService,
    private caseStateService: CaseStateService,
    private caseService: CaseService,
    private fileUtil: FileUtil,
    private financeBridgeService: FinanceBridgeService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private urlLocation: UrlLocation,
    private logger: NGXLogger,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {
    super();

    this.route.data.subscribe(({ supoCase }) => (this.draftCase = supoCase));
  }

  isDirty(): boolean {
    if (this.bypassDirtyCheck) {
      return false;
    }
    return (this.fg.dirty && this.fg.touched) || this.fileChanged;
  }

  ngOnInit(): void {
    this.caseId = !!this.draftCase ? null : this.route.parent?.parent?.snapshot.params['id'];

    this.bypassDirtyCheck = false;
    this.invoiceId = this.route.snapshot.params['invoice-id'];
    this.state = this.route.snapshot.data['state'];
    this.mode = this.route.snapshot.data['mode'];

    this.loadSeccaInvoices();
    this.initSupplierInvoice();

    this.fg = this.fb.group({
      creditorId: [null, [Validators.required]],
      invoiceDate: [null, [Validators.required]],
      invoiceNo: [null, [Validators.required]],
      items: this.fb.array([])
    });
    this.subscriptions.push(
      this.fg.controls.creditorId.valueChanges.subscribe(value => {
        this.supplierDetails = null;
        const selectedErpCreditor = this.supplierProfiles.flatMap(s => s.getErpCreditors()).find(c => c.erpId === +value);
        if (selectedErpCreditor) {
          if (!this.isBatchBilling) {
            this.supplierInvoice.invoiceAmountLocal.currency = selectedErpCreditor.currency;
          }
          this.enquireInvoiceNoUsed(value, null, false);
          this.fetchSupplierBankAccountDetails(value);
          this.isErpCreditorDisabled = selectedErpCreditor.erpActive === false;
        } else {
          this.isErpCreditorDisabled = false;
        }
      })
    );

    this.subscriptions.push(
      this.fg.valueChanges.subscribe(form => {
        if (this.supplierDetails?.paymentTerms && !!form.invoiceDate) {
          this.estimatedDueDate = calculateEstimatedDueDate(this.supplierDetails!.paymentTerms, new Date(form.invoiceDate));
        }
      })
    );

    let initObservable = this.initSupplierProfilesAndServiceOrders();
    if (!!this.invoiceId) {
      initObservable = this.initSupplierProfilesAndServiceOrders().pipe(
        mergeMap(() => this.loadInvoice(this.invoiceId))
      );
    }
    const initSub$ = this.handleObservableResponse(initObservable).subscribe(() => {
        this.isSupplierPartner =
          this.supplierProfiles.filter(supplierProfile => supplierProfile.type === this.partnerSupoTypeId)?.length > 0;
        this.isSupplierMedicalEscort =
          this.supplierProfiles.filter(supplierProfile => supplierProfile.type === this.medicalEscortSupoTypeId)?.length === this.supplierProfiles.length;

        this.setDropDownSuppliers();
        if (!this.invoiceId) {
          this.selectSupplierProfile();
          this.addItem();
        }
      });
    this.subscriptions.push(initSub$);
  }

  private setDropDownSuppliers() {
    const erpCreditors = this.supplierProfiles?.flatMap(profile => profile.getErpCreditors()) || [];

    const uniqueCreditors = new Map();
    erpCreditors.forEach(erpCreditor => uniqueCreditors.set(erpCreditor.erpId, erpCreditor));

    this.dropDownSuppliers  = Array.from(uniqueCreditors.values())
                                  .sort((c1,c2) => {
                                    return c1.erpId - c2.erpId;
                                  })
                                  .map(c => { return {
                                    id: c.erpId.toString(),
                                    name: `${c.erpId} - ${c.name} (${c.currency})`
                                  }});
  }

  private selectSupplierProfile() {
    if (this.isBatchBilling && this.draftCase) {
      const supplierProfile = this.supplierProfiles.find(s => s.supplierProfileCode === this.draftCase?.supplierProfileCode);
      this.fg.controls.creditorId.patchValue(supplierProfile.primaryErpCreditor.erpId.toString());
      this.fg.controls.creditorId.disable();
    } else if (this.supplierProfiles.length === 1) {
      this.fg.controls.creditorId.patchValue(this.supplierProfiles[0].primaryErpCreditor.erpId.toString());
      this.supplierInvoice.invoiceAmountLocal.currency = this.supplierProfiles[0].primaryErpCreditor.currency;
      if (!this.supplierProfiles[0].secondaryErpCreditors || this.supplierProfiles[0].secondaryErpCreditors.length === 0) {
        this.fg.controls.creditorId.disable();
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  submitInvoice() {
    if (!this.fg.valid) {
      this.validateAllFormFields(this.fg);
      return;
    }

    this.logger.info('Submitting invoice to FB');
    this.isSubmitRequest = true;
    if (this.isAmendingFinanceBridgeError()) {
      this.amendSupoInvoice();
    } else {
      this.save();
    }
  }

  private amendSupoInvoice() {
    if (this.saveInProgress) {
      return;
    }
    this.saveInProgress = true;
    const supoInvoice = {id: +this.invoiceId, invoiceNumber: this.fg.value.invoiceNo, creditorNumber: this.fg.value.creditorId} as SupoInvoice;
    this.supoPendingInvoicesService.amendInvoice(supoInvoice.id, supoInvoice).subscribe({
      next: () => {
        this.saveInProgress = false;
        this.cancel();
      },
      error: err => {
        this.error = err;
        this.saveInProgress = false;
      }
    })
  }

  save() {
    if (
      this.saveInProgress ||
      this.uploadComponentInstance?.files?.length > 1 ||
      this.uploadMedicalTravelComponentInstance?.files?.length > 1
    ) {
      return;
    }

    this.saveInProgress = true;
    this.saveInvoiceAndFiles()
      .then(() => {
        this.saveInProgress = false;
        this.bypassDirtyCheck = true;
        if (this.isSubmitRequest) {
          const modalRef = this.modalService.open(NewInvoiceSuccessModalComponent, {
            size: 'md',
            centered: true,
            scrollable: false
          });
          modalRef.componentInstance.caseId = this.route.snapshot.parent?.parent?.params['id'];
        } else {
          this.cancel();
        }
      })
      .catch(err => {
        this.error = err;
        this.isSubmitRequest = false;
        this.saveInProgress = false;
      });
  }

  submitCaseAndInvoice() {
    if (
      this.saveInProgress ||
      this.uploadComponentInstance?.files?.length > 1 ||
      this.uploadMedicalTravelComponentInstance?.files?.length > 1
    ) {
      return;
    }

    if (!this.fg.valid) {
      this.validateAllFormFields(this.fg);
      return;
    }

    if (this.isBatchBilling && this.draftCase) {
      this.logger.info('Submitting case to CCCBridge and saving invoice');
      this.saveInProgress = true;

      this.submitCaseToCCCBridgeAndSaveInvoice(this.draftCase)
        .then(() => {
          this.saveInProgress = false;
          this.bypassDirtyCheck = true;
          this.modalService.open(BatchBillingSuccessModalComponent, {
            size: 'lg',
            centered: true,
            scrollable: false
          });
        })
        .catch(err => {
          this.error = err;
          this.isSubmitRequest = false;
          this.saveInProgress = false;
        });
    }
  }

  delete() {
    if (this.supplierInvoice.id && this.state === 'draft') {
      const modalRef = this.modalService.open(ConfirmModalComponent);
      const configuration = new ModalDialogConfiguration({
        body: 'Are you sure you want to delete the invoice?'
      });
      modalRef.componentInstance.init(configuration);
      modalRef.result.then(result => {
        if (result) {
          this.deleteInvoice();
        }
      });
    }
  }

  isEven(i: number): boolean {
    return i % 2 === 0;
  }

  isOdd(i: number): boolean {
    return i % 2 !== 0;
  }

  sortedBy(column: string): string {
    return this.sortedOn === column ? 'sorted-by' : '';
  }

  sortBy(column: string): void {
    this.sortedOn = column;
    if (this.sortOrder[column] == null) {
      this.sortOrder[column] = SortOrderType.asc;
    } else {
      if (this.sortOrder[column] === SortOrderType.asc) {
        this.sortOrder[column] = SortOrderType.desc;
      } else {
        this.sortOrder[column] = SortOrderType.asc;
      }
    }

    this.sortInvoices();
  }

  checkInvoiceNo(evnt: any): void {
    const value = evnt.target.value;
    this.enquireInvoiceNoUsed(null, value, false);
  }

  onInvoiceFileSelected() {
    this.invoiceFilesValidationResult = this.fileUtil.validateFiles(this.uploadComponentInstance.files, new FileUtilValidationConfig({maxFileSize: 12582912, allowedMimeTypes: ['application/pdf']}));
  }

  onTravelAndExpenseFileSelected() {
    this.travelAndExpenseFilesValidationResult = this.fileUtil.validateFiles(this.uploadTravelExpensesReportComponentInstance.files, new FileUtilValidationConfig({maxFileSize: 12582912, allowedMimeTypes: this.acceptedTravelAndExpensesDocumentTypes}));
  }

  onMedicalTravelReportFileSelected() {
    this.medicalTravelReportFilesValidationResult = this.fileUtil.validateFiles(this.uploadMedicalTravelComponentInstance.files, new FileUtilValidationConfig({maxFileSize: 12582912, allowedMimeTypes: ['application/pdf']}));
  }

  getAcceptedTravelAndExpensesDocumentTypes() {
    return this.acceptedTravelAndExpensesDocumentTypes.join(',');
  }

  get invalidInvoiceFiles(): string[] | undefined {
    return this.fileUtil.getInvalidFiles(this.invoiceFilesValidationResult);
  }

  get maxSizeExceededInvoiceFiles(): string[] | undefined {
    return this.fileUtil.getMaxSizeExceededFiles(this.invoiceFilesValidationResult);
  }

  get maxSizeInvoiceFiles() {
    return this.fileUtil.getMaxFileSizeAsMB(this.invoiceFilesValidationResult);
  }

  get allowedFileTypesInvoiceFiles() {
    return this.fileUtil.getAllowedFilesTypes(this.invoiceFilesValidationResult);
  }

  get invalidTravelAndExpenseFiles(): string[] | undefined {
    return this.fileUtil.getInvalidFiles(this.travelAndExpenseFilesValidationResult);
  }

  get maxSizeExceededTravelAndExpenseFiles(): string[] | undefined {
    return this.fileUtil.getMaxSizeExceededFiles(this.travelAndExpenseFilesValidationResult);
  }

  get maxSizeTravelAndExpenseFiles() {
    return this.fileUtil.getMaxFileSizeAsMB(this.travelAndExpenseFilesValidationResult);
  }

  get allowedFileTypesTravelAndExpenseFiles() {
    return this.fileUtil.getAllowedFilesTypes(this.travelAndExpenseFilesValidationResult);
  }

  get invalidMedicalTravelReportFiles(): string[] | undefined {
    return this.fileUtil.getInvalidFiles(this.medicalTravelReportFilesValidationResult);
  }

  get maxSizeExceededMedicalTravelReportFiles(): string[] | undefined {
    return this.fileUtil.getMaxSizeExceededFiles(this.medicalTravelReportFilesValidationResult);
  }

  get maxSizeMedicalTravelReportFiles() {
    return this.fileUtil.getMaxFileSizeAsMB(this.medicalTravelReportFilesValidationResult);
  }

  get allowedFileTypesMedicalTravelReportFiles() {
    return this.fileUtil.getAllowedFilesTypes(this.medicalTravelReportFilesValidationResult);
  }

  getFilename(array: string[] | undefined): string {
    return array?.join(',');
  }

  get isBatchBilling(): boolean {
    return this.mode === NewInvoiceComponent.MODE_BATCH_BILLING;
  }

  get isBatchBillingActive(): boolean {
    return (
      this.isAuthenticated() &&
      !!this.supplierProfiles.find(s => s.type === NewInvoiceComponent.BATCH_BILLING_TYPE_ID) &&
      !!this.accountRoleService.roles.canCreateEconomyPageForTravel
    );
  }

  get isDraft(): boolean {
    return this.state === 'draft';
  }

  private initSupplierProfilesAndServiceOrders(): Observable<void> {
    let sources: Observable<any[]>[] = [this.fetchSupplierTypes(), this.fetchSupplierProfiles()];
    if (!this.isBatchBilling) {
      sources.push(this.fetchServiceOrders());
    }
    return combineLatest(sources)
      .pipe(
        tap((results: any[]) => this.supplierProfiles = results[1]),
        map(() => {})
      );
  }

  private fetchSupplierProfiles(): Observable<SupplierProfile[]> {
    return this.supplierProfileService.getSupplierProfiles(this.caseId).pipe(take(1));
  }

  private fetchSupplierTypes(): Observable<SupoType[]> {
    return this.masterListService.getSupoTypes().pipe(
      tap(supoTypes => {
        this.partnerSupoTypeId = supoTypes.find(s => s.code === SupoTypeEnum.PARTNER)?.id;
        this.medicalEscortSupoTypeId = supoTypes.find(s => s.code === SupoTypeEnum.MEDICAL_ESCORT)?.id;
      })
    );

  }

  private fetchServiceOrders(): Observable<IServiceOrder[]> {
    if (this.isBatchBilling) {
      of([]);
    }

    return this.caseStateService.getCase()
      .pipe(
        takeWhile(() => !this.isDestroyed),
        take(1),
        tap(caseResult => this.seccaCase = caseResult),
        mergeMap(caseResult => this.caseService.getServiceOrders(caseResult.id)),
        tap(serviceOrders => this.serviceOrders = serviceOrders)
      );
  }

  private initSupplierInvoice(): void {
    this.supplierInvoice = new SupplierInvoice({
      invoiceDocument: null,
      travelAndExpensesDocuments: null,
      medicalTravelReportDocument: null,
      invoiceAmountLocal: { amount: 0, currency: '', precision: 2 }
    });

    if (this.isBatchBilling && this.draftCase) {
      if (this.draftCase.medical.expectedExpensesCurrency) {
        this.supplierInvoice.invoiceAmountLocal.currency = this.draftCase.medical.expectedExpensesCurrency;
      }
      if (this.draftCase.medical.expectedExpensesAmount) {
        this.supplierInvoice.invoiceAmountLocal.amount = this.draftCase.medical.expectedExpensesAmount;
      }
    }
  }

  private sortInvoices(): void {
    this.otherInvoicesOnCase.sort((role1: SupplierInvoice, role2: SupplierInvoice) => {
      if (this.sortedOn === 'createSubmitDate' && role1.supoCreatedDate && role2.supoCreatedDate) {
        return (
          (this.sortOrder[this.sortedOn] === SortOrderType.asc ? 1 : -1) * (role1.supoCreatedDate ? role1.supoCreatedDate.getTime() : 0) -
          (role2.supoCreatedDate ? role2.supoCreatedDate.getTime() : 0)
        );
      }
      if (typeof role1[this.sortedOn] === 'string') {
        return (this.sortOrder[this.sortedOn] === SortOrderType.asc ? 1 : -1) * role1[this.sortedOn].localeCompare(role2[this.sortedOn]);
      } else {
        return (this.sortOrder[this.sortedOn] === SortOrderType.asc ? 1 : -1) * role1[this.sortedOn] - role2[this.sortedOn];
      }
    });
  }

  private loadSeccaInvoices(): void {
    if (this.isBatchBilling) {
      return;
    }
    this.subscriptions.push(
      combineLatest([
        this.supplierInvoiceStateService.getSupplierInvoices(),
        this.supplierProfileService.getSupplierProfiles(this.caseId),
        this.supoInvoiceStateService.getSupoInvoices()
      ])
        .pipe(
          map(([invoices, suppliers, supoInvoices]) => {
            const mappedInvoices = invoices.map(invoice => {
              let totalSavings = 0;
              invoice.items.forEach(item =>
                item.savings.filter(saving => !saving.gross).forEach(saving => (totalSavings += +saving.amount))
              );
              invoice.settled = +invoice.invoiceAmountLocal.amount - +totalSavings;
              return invoice;
            });
            const mapper = new SupoSeccaInvoiceMapper(suppliers);
            const supoSupplierInvoices = supoInvoices.map((supoInvoice: SupoInvoice) => mapper.mapToSupplierInvoice(supoInvoice));
            return mappedInvoices.concat(supoSupplierInvoices).filter(invoice => invoice.id.toString() !== this.invoiceId);
          })
        )
        .subscribe(result => {
          this.otherInvoicesOnCase = result;
        })
    );
  }

  private loadInvoice(invoiceId: any): Observable<void> {
    if (!this.supplierProfiles || !this.serviceOrders || invoiceId === undefined) {
      return of();
    }
    if (this.state === 'draft') {
      return this.supoCaseInvoiceService.getSupoInvoice(invoiceId).pipe(map(supoInvoice => this.patchWithSupoInvoice(supoInvoice)));
    } else if (this.state === 'pending') {
      return this.supoPendingInvoicesService.getPendingInvoice(invoiceId).pipe(map(supoInvoice => this.patchWithSupoInvoice(supoInvoice)));
    } else {
      return this.supplierInvoicesService.getInvoice(invoiceId).pipe(map(supplierInvoice => this.patchWithSupplierInvoice(supplierInvoice)));
    }
  }

  createInvoiceItem(): UntypedFormControl {
    const newItem = new SupplierInvoiceItem();
    if (this.fg?.controls['items']) {
      let newId = -1;
      for (let i = 0; i < this.items.length; i++) {
        const existing = this.items.at(i).value;
        if (+existing.id <= newId) {
          newId = +existing.id - 1;
        }
      }
      newItem.id = newId;
    } else {
      newItem.id = -1;
    }
    return this.fb.control(newItem);
  }

  serviceOrderChanged(event: any): void {
    this.setMedicalEscortSelected();
  }

  private setMedicalEscortSelected(): void {
    this.isMedicalEscortSelected = !!this.items.value.find((item: any) => {
      return item.serviceOrderType === ServiceTypeEnum.MEDICAL_ESCORT;
    });
  }

  private patchWithSupoInvoice(supoInvoice: SupoInvoice) {
    if (supoInvoice) {
      this.mapper = new SupoSeccaInvoiceMapper(this.supplierProfiles);
      this.patchWithSupplierInvoice(this.mapper.mapToSupplierInvoice(supoInvoice));

      this.isSupoInvoiceFinanceBridgeError = supoInvoice.financeBridgeAcknowledgeStatus === 'ERROR';
      if (this.isAmendingFinanceBridgeError()) {
        this.fg.controls.creditorId.enable();
        this.fg.controls.invoiceNo.enable();
      }
    }
  }

  isAmendingFinanceBridgeError() {
    return this.state === 'pending' && this.isSupoInvoiceFinanceBridgeError && this.accountService.isAdmin();
  }

  private patchWithSupplierInvoice(supplierInvoice: SupplierInvoice | undefined) {
    if (supplierInvoice) {
      this.supplierInvoice = supplierInvoice;
      if (supplierInvoice.invoiceStatus) {
        this.lastInvoiceEnquiry.creditorId = supplierInvoice.creditorId;
        this.lastInvoiceEnquiry.invoiceNo = supplierInvoice.invoiceNumber;
      }
      this.fg.controls.creditorId.patchValue(this.supplierInvoice.creditorId);
      this.fg.controls.invoiceDate.patchValue(this.supplierInvoice.invoiceDate);
      this.fg.controls.invoiceNo.patchValue(this.supplierInvoice.invoiceNumber);
      this.supplierInvoice.items.forEach(item => this.items.push(this.fb.control(item)));

      if (this.state !== 'draft') {
        this.fg.disable();
      }
    }
  }

  private deleteInvoice(): void {
    this.handleObservableResponse(this.supoCaseInvoiceService.deleteInvoice('' + this.supplierInvoice.id)).subscribe(() => {
      this.bypassDirtyCheck = true;
      this.router.navigate(['case', this.seccaCase?.id, 'economy', 'invoices', 'drafts']);
    });
  }

  private async submitCaseToCCCBridgeAndSaveInvoice(draftCase: SupoCase): Promise<any> {
    const supoCase = await this.submitCaseToCCCBridge(draftCase, ChannelType.BATCH_BILLING);

    if (supoCase.cccBridgeToken) {
      this.batchBillingCaseId = supoCase.cccBridgeToken.substring('Token_'.length); // Todo: do not use the cccBridgeToken to extract case id!
    }

    await this.saveInvoiceAndFiles();

    await this.approveCase(supoCase);
  }

  private async saveInvoiceAndFiles() {
    const supoInvoice = this.getSupoInvoice();

    let persistFunction = null;
    if (supoInvoice) {
      persistFunction = this.supoCaseInvoiceService.createInvoice(supoInvoice);
      if (supoInvoice.id) {
        persistFunction = this.supoCaseInvoiceService.updateInvoice(supoInvoice);
      }

      const savedSupoInvoice = await persistFunction.toPromise();

      await this.saveFiles(savedSupoInvoice);
    }
  }

  private async submitCaseToCCCBridge(draftCase: SupoCase, channel?: string) {
    return this.supoCaseInvoiceService.sendToCCCBridge(draftCase, this.isAuthenticated(), channel).toPromise();
  }

  private async approveCase(draftCase: SupoCase) {
    return this.supoCaseInvoiceService.approveCase(draftCase, this.isAuthenticated()).toPromise();
  }

  private getSupoInvoice(): SupoInvoice | null {
    if (this.seccaCase || this.isBatchBilling) {
      const values = this.fg.getRawValue(); // getRawValue also gives disabled controls values
      let supplierProfileCode: string | undefined;
      let creditorId = values.creditorId;
      if (this.supplierProfiles.length === 1) {
        supplierProfileCode = this.supplierProfiles[0].supplierProfileCode;
        creditorId = this.supplierProfiles[0].primaryErpCreditor.erpId;
      } else {
        supplierProfileCode = this.supplierProfiles.find(sp => sp.getErpCreditors().map(c => c.erpId).includes(+values.creditorId))?.supplierProfileCode;
      }
      if (!supplierProfileCode) {
        throw new Error('supplier profile not found for creditor id ' + values.creditorId);
      }
      values.items
        .filter((item: SupplierInvoiceItem) => item.calculatedFromGross)
        .forEach((item: SupplierInvoiceItem) => {
          const grossSavings = item.savings.filter(s => s.gross).sort((s1, s2) => s2.lineNumber - s1.lineNumber);
          let paidAmount = 0;
          for (let i = 0; i < grossSavings.length; i++) {
            if (i === 0 && grossSavings[i].totalAmount) {
              // @ts-ignore
              paidAmount = grossSavings[i].totalAmount;
            }
            paidAmount -= grossSavings[i].amount;
          }
          item.paidAmount = paidAmount;
        });

      const supoInvoice: SupoInvoice = {
        id: this.supplierInvoice.id,
        createdOn: new Date(),
        supplierProfileCode,
        invoiceDate: values.invoiceDate,
        invoiceNumber: values.invoiceNo,
        caseReference: this.seccaCase ? this.seccaCase.caseNumber : null,
        invoiceAmount: this.getTotalPaidAmount(),
        invoiceCurrency: this.supplierInvoice.invoiceAmountLocal.currency,
        items: values.items,
        creditorNumber: creditorId,
        invoiceDocuments: [],
        supoCase: null,
        submittedToFinanceBridge: null,
        financeBridgeAcknowledgeStatus: null,
        financeBridgeAcknowledgeText: null,
        financeBridgeAcknowledgeTime: null,
        isBatchBilling: this.isBatchBilling,
        batchBillingCaseId: this.isBatchBilling ? this.batchBillingCaseId : null
      };
      supoInvoice.items.filter(item => item.id && item.id < 0).forEach(item => (item.id = null));

      if (this.supplierInvoice.invoiceDocument) {
        supoInvoice.invoiceDocuments?.push(this.supplierInvoice.invoiceDocument);
      }

      const isMedicalEscort = values.items.find((item: any) => item.serviceOrderType === ServiceTypeEnum.MEDICAL_ESCORT);
      if (isMedicalEscort) {
        if (this.supplierInvoice.travelAndExpensesDocuments && this.supplierInvoice.travelAndExpensesDocuments?.length > 0) {
          supoInvoice.invoiceDocuments?.push(...this.supplierInvoice.travelAndExpensesDocuments);
        }
        if (this.supplierInvoice.medicalTravelReportDocument) {
          supoInvoice.invoiceDocuments?.push(this.supplierInvoice.medicalTravelReportDocument);
        }
      }
      return supoInvoice;
    }
    return null;
  }

  addItem() {
    this.items.push(this.createInvoiceItem());
  }

  isApproved(): boolean {
    return this.state !== 'draft';
  }

  deleteItem(item: SupplierInvoiceItem) {
    const toDelete = this.items.value.findIndex((invoiceItem: SupplierInvoiceItem) => invoiceItem.id === item.id);
    if (toDelete > -1) {
      this.items.removeAt(toDelete);
    }

    this.setMedicalEscortSelected();
  }

  getTotalReductions(): number {
    let total: number = 0;
    if (this.fg.disabled) {
      this.supplierInvoice.items.forEach(item =>
        item.savings?.filter(saving => !saving.gross).forEach(saving => (total = +total + +saving.amount))
      );
    } else {
      this.items.value.forEach((item: SupplierInvoiceItem) =>
        item.savings?.filter(saving => !saving.gross).forEach(saving => (total = +total + +saving.amount))
      );
    }
    return total;
  }

  getTotalReductionsPct(): number {
    const totalPaid = this.getTotalPaidAmount();
    const reduction = this.getTotalReductions();
    return Math.round((+reduction * 1000) / +totalPaid) / 10;
  }

  getTotalPaidAmount(): number {
    let total: number = 0;
    if (this.fg.disabled) {
      this.supplierInvoice.items.forEach(item => (total = +total + +item.paidAmount));
    } else {
      this.items.value.forEach((item: SupplierInvoiceItem) => {
        if (item.calculatedFromGross) {
          const grossSavings = item.savings.filter(s => s.gross).sort((s1, s2) => s2.lineNumber - s1.lineNumber);
          for (let i = 0; i < grossSavings.length; i++) {
            if (i === 0 && grossSavings[i].totalAmount) {
              // @ts-ignore
              total += grossSavings[i].totalAmount;
            }
            total -= +grossSavings[i].amount;
          }
        } else {
          total += +item.paidAmount;
        }
      });
    }
    return total;
  }

  getTotalGrossAmount(): number {
    return this.getTotalPaidAmount() + this.getTotalGrossSavings();
  }

  getTotalGrossSavings(): number {
    let grossSavings: number = 0;
    if (this.fg.disabled) {
      this.supplierInvoice.items.forEach(item =>
        item.savings?.filter(saving => saving.gross).forEach(saving => (grossSavings = +grossSavings + +saving.amount))
      );
    } else {
      this.items.value.forEach((item: SupplierInvoiceItem) =>
        item.savings?.filter(saving => saving.gross).forEach(saving => (grossSavings = +grossSavings + +saving.amount))
      );
    }
    return grossSavings;
  }

  getTotalGrossSavingsPct(): number {
    const totalPaid = this.getTotalPaidAmount();
    const grossSaving = this.getTotalGrossSavings();
    return Math.round((+grossSaving * 1000) / (+totalPaid + +grossSaving)) / 10;
  }

  getTotalNetAmount(): number {
    return this.getTotalPaidAmount() - this.getTotalReductions();
  }

  cancel() {
    this.urlLocation.back();
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  protected onError(error: any) {
    this.error = error;
    this.isSubmitRequest = false;
    this.saveInProgress = false;
  }

  private async saveFiles(supoInvoice: SupoInvoice): Promise<any> {
    await Promise.all([
      this.saveInvoiceFiles(supoInvoice),
      this.saveTravelAndExpensesFiles(supoInvoice),
      this.saveMedicalTravelReportFiles(supoInvoice)
    ]);
    if (this.isSubmitRequest) {
      await this.doSubmitToFinanceBridge(supoInvoice).toPromise();
    }
  }

  private async saveInvoiceFiles(supoInvoice: SupoInvoice): Promise<any> {
    if (this.uploadComponentInstance?.files?.length > 0) {
      for (const file of this.uploadComponentInstance?.files) {
        if (file && supoInvoice.id) {
          await this.saveDocument(supoInvoice, file, InvoiceDocumentTypeEnum.INVOICE);
        }
      }
    }
  }

  private async saveTravelAndExpensesFiles(supoInvoice: SupoInvoice): Promise<any> {
    let documents: SupoDocument[] | null = supoInvoice.invoiceDocuments;
    if (this.uploadTravelExpensesReportComponentInstance?.files?.length > 0) {
      documents = [];
      for (const file of this.uploadTravelExpensesReportComponentInstance?.files) {
        if (file && supoInvoice.id) {
          const document = await this.saveDocument(supoInvoice, file, InvoiceDocumentTypeEnum.TRAVEL_AND_EXPENSES);
          documents.push(document);
        }
      }
    }

    if (this.isSubmitRequest && documents) {
      const travelAndExpensesDocuments = documents.filter(
        document => document.documentType === InvoiceDocumentTypeEnum.TRAVEL_AND_EXPENSES
      );
      for (const document of travelAndExpensesDocuments) {
        await this.addDocumentToCase(supoInvoice.id, CaseDocumentEnum.ESCORT_TRAVEL_EXPENSES, document).toPromise();
      }
    }
  }

  private async saveMedicalTravelReportFiles(supoInvoice: SupoInvoice): Promise<any> {
    let documents: SupoDocument[] | null = supoInvoice.invoiceDocuments;
    if (this.uploadMedicalTravelComponentInstance?.files?.length > 0) {
      documents = [];
      for (const file of this.uploadMedicalTravelComponentInstance?.files) {
        if (file && supoInvoice.id) {
          const document = await this.saveDocument(supoInvoice, file, InvoiceDocumentTypeEnum.MEDICAL_TRAVEL_REPORT);
          documents.push(document);
        }
      }
    }

    if (this.isSubmitRequest && documents) {
      await this.addDocumentToCase(
        supoInvoice.id,
        CaseDocumentEnum.MEDICAL_INFO,
        documents?.find(document => document.documentType === InvoiceDocumentTypeEnum.MEDICAL_TRAVEL_REPORT) || null
      ).toPromise();
    }
  }

  private addDocumentToCase(
    invoiceId: number | null,
    documentTag: string,
    document: SupoDocument | null
  ): Observable<AttachInvoiceDocument | void> {
    if (document && this.seccaCase?.id && invoiceId) {
      const invoiceDocument: AttachInvoiceDocument = {
        caseId: +this.seccaCase?.id,
        invoiceId,
        documentTag: documentTag ? Object.keys(CaseDocumentEnum).find(x => CaseDocumentEnum[x] === documentTag) : null,
        document: document
      };

      return this.handleObservableResponse(this.supoCaseInvoiceService.addDocumentToCase(invoiceDocument));
    }

    return of();
  }

  private doSubmitToFinanceBridge(supoInvoice: SupoInvoice): Observable<SupoInvoice | void> {
    if (!this.invoiceNoUsedMsg) {
      return this.handleObservableResponse(this.supoCaseInvoiceService.sendToFinanceBridge(supoInvoice));
    }
    return of();
  }

  private async saveDocument(supoInvoice: SupoInvoice, invoiceFile: File, documentType: InvoiceDocumentTypeEnum): Promise<SupoDocument> {
    const invoiceDocument = await this.fileUtil.observableOfFile(invoiceFile).toPromise();
    if (supoInvoice.id) {
      invoiceDocument.documentType = documentType;
      const supoDocument = await this.supoCaseInvoiceService.addDocumentToInvoice(invoiceDocument, supoInvoice.id).toPromise();
      return supoDocument;
    }

    return {} as SupoDocument;
  }

  getAndOpenDocument(document: SupoDocument | null) {
    if (document?.id) {
      this.supoCaseInvoiceService.getDocument(document.id).subscribe(
        result => {
          const url = window.URL.createObjectURL(result);
          window.open(url);
        },
        error => this.logger.debug(error)
      );
    }
  }

  deleteDocument(): void {
    this.supplierInvoice.invoiceDocument = null;
    this.fileChanged = true;
  }

  deleteMedicalTravelReportDocument(): void {
    this.supplierInvoice.medicalTravelReportDocument = null;
    this.fileChanged = true;
  }

  deleteTravelAndExpensesDocument(id: number): void {
    this.supplierInvoice.travelAndExpensesDocuments =
      this.supplierInvoice.travelAndExpensesDocuments?.filter(document => document.id !== id) || null;
    this.fileChanged = true;
  }

  validationError(control: string): boolean {
    const ctl = this.fg.get(control);
    if (ctl) {
      return ctl.invalid && ctl.touched;
    }
    return false;
  }

  getDisplayState(): string {
    if (this.state === 'received-in-secca') {
      switch (this.supplierInvoice.invoiceStatus) {
        case SupplierInvoiceStatus.rejected:
        case SupplierInvoiceStatus.customerCharged:
        case SupplierInvoiceStatus.supplierPaid:
          return 'Closed';
        default:
          return 'Open';
      }
    } else if (this.supplierInvoice.submittedToFinanceBridge) {
      return 'Pending';
    }
    return 'New';
  }

  private enquireInvoiceNoUsed(creditorId: number | null, invoiceNo: string | null, enquireWithSuffix: boolean): void {
    creditorId = creditorId ? creditorId : this.fg.controls.creditorId.value;
    invoiceNo = invoiceNo ? invoiceNo : this.fg.controls.invoiceNo.value;

    if (
      !!creditorId &&
      !!invoiceNo &&
      (creditorId !== this.lastInvoiceEnquiry.creditorId || invoiceNo !== this.lastInvoiceEnquiry.invoiceNo)
    ) {
      this.invoiceNoLookupInProgress = true;
      this.financeBridgeService
        .supplierInvoiceLookup(creditorId, invoiceNo)
        .pipe(finalize(() => (this.invoiceNoLookupInProgress = false)))
        .subscribe(
          response => {
            if (response.invoiceSuffix && !enquireWithSuffix) {
              const today = new Date();
              this.enquireInvoiceNoUsed(creditorId, invoiceNo + '-' + today.getFullYear(), true);
            } else {
              this.invoiceNoUsedMsg = 'The invoice number is already registered in our system. Please update invoice number';
            }
          },
          error => {
            if (error.status === 403) {
              this.invoiceNoUsedMsg = 'invoice number must start and end with an alphanumeric character (a-z, A-Z or 0-9)';
            } else if (error.status === 404) {
              this.lastInvoiceEnquiry.creditorId = creditorId;
              this.lastInvoiceEnquiry.invoiceNo = invoiceNo;
              this.invoiceNoUsedMsg = undefined;
              if (enquireWithSuffix) {
                this.fg.controls.invoiceNo.patchValue(invoiceNo);
              }
            }
          }
        );
    }
  }

  public getSimplifiedStatus(supplierInvoice: SupplierInvoice): string {
    switch (supplierInvoice.invoiceStatus) {
      case SupplierInvoiceStatus.invoiceReceived:
        return 'Received';
      case SupplierInvoiceStatus.awaitingAccounting:
      case SupplierInvoiceStatus.invoiceCorrected:
      case SupplierInvoiceStatus.ongoingCostControl:
        return 'Ongoing';
      case SupplierInvoiceStatus.rejected:
        return 'Rejected';
      case SupplierInvoiceStatus.approved:
        return 'Approved';
      case SupplierInvoiceStatus.customerCharged:
      case SupplierInvoiceStatus.supplierPaid:
        return 'Paid';
      default:
        if (!!supplierInvoice.submittedToFinanceBridge) {
          return 'Pending';
        }
        return 'Draft';
    }
  }

  private fetchSupplierBankAccountDetails(creditorId: number): void {
    this.error404 = false;
    this.error503 = false;
    const subscription = this.financeBridgeService.fetchSupplierBankAccountDetails(creditorId).subscribe(
      supplierDetails => {
        this.supplierDetails = supplierDetails;
        if (this.fg.getRawValue().invoiceDate) {
          this.estimatedDueDate = calculateEstimatedDueDate(this.supplierDetails.paymentTerms, new Date(this.fg.getRawValue().invoiceDate));
        }
        subscription.unsubscribe();
      },
      error => {
        if (error.status === 404) {
          this.error404 = true;
        } else if (error.status === 503) {
          this.error503 = true;
        }
      }
    );
  }

  isSaveDraftButtonPopoverDisabled(): boolean {
    return !this.saveDisabled();
  }

  isSubmitButtonPopoverDisabled(): boolean {
    return !this.submitDisabled();
  }

  getSaveDraftButtonDisabledText(): string {
    const messages = [];

    if (!this.fg.get('creditorId')?.value) {
      messages.push(this.translateService.instant('xx is a required field', { type: this.translateService.instant('Creditor') }));
    }

    if (this.isMedicalEscortAndMultipleItems()) {
      messages.push(this.translateService.instant('Medical escort can not have multiple items'));
    }

    return messages.join('<br>');
  }

  getMaximumInvoiceDate(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  getMinimumInvoiceDate(): Date {
    const today = new Date();
    today.setDate(today.getDate() - 3650);
    return today;
  }

  getSubmitButtonDisabledText(): string {
    const messages = [];

    if (this.error404 || this.error503) {
      messages.push(this.translateService.instant('Not available - see description above'));
    }

    if (!this.fg.get('creditorId')?.value) {
      messages.push(this.translateService.instant('xx is a required field', { type: this.translateService.instant('Creditor') }));
    }

    if (!this.fg.get('invoiceDate')?.value) {
      messages.push(this.translateService.instant('xx is a required field', { type: this.translateService.instant('Invoice date') }));
    } else {
      var varDate = new Date(this.fg.get('invoiceDate')?.value);
      if (varDate < this.getMinimumInvoiceDate()) {
        messages.push(this.translateService.instant('Invoice date to old'));
      }
      if (varDate > this.getMaximumInvoiceDate()) {
        messages.push(this.translateService.instant('Invoice date in the future'));
      }
    }

    if (!this.fg.get('invoiceNo')?.value) {
      messages.push(this.translateService.instant('xx is a required field', { type: this.translateService.instant('Invoice no.') }));
    }

    if (!this.fg.get('invoiceNo')?.valid) {
      messages.push(this.translateService.instant('invoice number must start and end with an alphanumeric character (a-z, A-Z or 0-9)'));
    }

    if (this.isItemsMissingFields()) {
      messages.push(this.translateService.instant('Items are missing required fields'));
    }

    if (this.isItemsQuantityEq0()) {
      messages.push(this.translateService.instant('Items quantity can not be 0'));
    }

    if (this.isSavingsMissingFields()) {
      messages.push(this.translateService.instant('Savings are missing required fields'));
    }

    if (this.isSavingsGt100()) {
      messages.push(this.translateService.instant('Savings can not be greater than 100%'));
    }

    if (this.isMedicalEscortAndMultipleItems()) {
      messages.push(this.translateService.instant('Medical escort can not have multiple items'));
    }

    if (this.uploadComponentInstance.files.length !== 1 && !this.supplierInvoice.invoiceDocument) {
      messages.push(this.translateService.instant('Missing xx documents', { type: this.translateService.instant('Invoice') }));
    }

    if ( this.hasUploadErrors(this.invoiceFilesValidationResult) ) {
      messages.push(this.translateService.instant('There are invoice document upload errors'));
    }

    if ( this.hasUploadErrors(this.travelAndExpenseFilesValidationResult) ) {
      messages.push(this.translateService.instant('There are travel and expenses document upload errors'));
    }

    if ( this.hasUploadErrors(this.medicalTravelReportFilesValidationResult) ) {
      messages.push(this.translateService.instant('There are medical travel report document upload errors'));
    }

    if (
      this.isMedicalEscortSelected &&
      this.uploadTravelExpensesReportComponentInstance?.files?.length === 0 &&
      (!this.supplierInvoice.travelAndExpensesDocuments || this.supplierInvoice.travelAndExpensesDocuments?.length === 0)
    ) {
      messages.push(
        this.translateService.instant('Missing xx documents', { type: this.translateService.instant('Travel and expenses documentation') })
      );
    }

    if (
      this.isMedicalEscortSelected &&
      this.uploadMedicalTravelComponentInstance?.files?.length !== 1 &&
      !this.supplierInvoice.medicalTravelReportDocument
    ) {
      messages.push(
        this.translateService.instant('Missing xx documents', { type: this.translateService.instant('Medical travel report') })
      );
    }

    if (!!this.invoiceNoUsedMsg) {
      messages.push(this.translateService.instant('The invoice number is already registered'));
    }

    if (this.isErpCreditorDisabled) {
      messages.push(this.translateService.instant('Selected creditor is not active'));
    }

    if ( this.getTotalNetAmount() === 0 ) {
      messages.push(this.translateService.instant('Total paid amount cannot be 0'));
    }


    return messages.join('<br>');
  }

  saveDisabled(): boolean {
    return this.saveInProgress || !this.fg.get('creditorId')?.value || this.isMedicalEscortAndMultipleItems();
  }

  submitDisabled(): boolean {
    var varDate = new Date(this.fg.get('invoiceDate')?.value);

    let submitDisabled = false;
    if (!this.isAmendingFinanceBridgeError()) {
      submitDisabled = (this.uploadComponentInstance?.files?.length !== 1 && !this.supplierInvoice.invoiceDocument || this.hasUploadErrors(this.invoiceFilesValidationResult)) ||
      (this.isMedicalEscortSelected &&
        ((this.uploadTravelExpensesReportComponentInstance?.files?.length === 0 &&
            this.supplierInvoice.travelAndExpensesDocuments?.length === 0) ||
          (this.uploadMedicalTravelComponentInstance?.files?.length !== 1 && !this.supplierInvoice.medicalTravelReportDocument) ||
          (this.hasUploadErrors(this.travelAndExpenseFilesValidationResult) || this.hasUploadErrors(this.medicalTravelReportFilesValidationResult)))
      ) ||
      this.isItemsMissingFields() ||
      this.isItemsQuantityEq0() ||
      this.getTotalNetAmount() === 0 ||
      this.isMedicalEscortAndMultipleItems() ||
      varDate < this.getMinimumInvoiceDate() ||
      varDate > this.getMaximumInvoiceDate() ||
      this.isErpCreditorDisabled ||
      !!this.invoiceNoUsedMsg;
    }

    return (
      this.error404 ||
      this.error503 ||
      this.saveInProgress ||
      this.invoiceNoLookupInProgress ||
      !this.fg.valid ||
      submitDisabled
    );
  }

  private hasUploadErrors(validationResult: Map<string, string[]>) {
    return this.fileUtil.getInvalidFiles(validationResult)?.length > 0 ||
           this.fileUtil.getMaxSizeExceededFiles(validationResult)?.length > 0;
  }

  isItemsMissingFields(): boolean {
    return !!this.items.value.find(
      (item: any) =>
        !item.serviceOrderId || !item.serviceItemCode || (!item.quantity && item.quantity !== 0) || !item.quantityUnit || !item.paidAmount
    );
  }

  isItemsQuantityEq0(): boolean {
    return !!this.items.value.find((item: any) => item.quantity === 0);
  }

  isSavingsMissingFields(): boolean {
    return !!this.items.value.find(
      (item: any) =>
        !!item.savings?.find((saving: any) => !saving.reductionTypeCode || !saving.amount || !saving.percentage /*|| !saving.totalAmount*/)
    );
  }

  isSavingsGt100(): boolean {
    return !!this.items.value.find(
      (item: any) => !!item.savings?.find((saving: any) => !!saving.percentage && isFinite(saving.percentage) && saving.percentage > 100)
    );
  }

  isMedicalEscortAndMultipleItems(): boolean {
    if (this.isMedicalEscortSelected && this.items.length > 1) {
      const medicalEscortItem = this.items.value.find((item: any) => item.serviceOrderType === ServiceTypeEnum.MEDICAL_ESCORT);
      return !!this.items.value.find(
        (item: any) =>
          !!item.serviceOrderType &&
          (item.serviceOrderType !== ServiceTypeEnum.MEDICAL_ESCORT || item.serviceOrderId !== medicalEscortItem.serviceOrderId)
      );
    }

    return false;
  }
}
